import React, { useEffect } from 'react';
import { FormattedMessage } from 'react-intl';

import Button from '@atlaskit/button/new';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, xcss } from '@atlaskit/primitives';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { token } from '@atlaskit/tokens';

import { messages } from '../../messages.ts';
import { WorkItemCreateErrorImage } from '../../assets/WorkItemCreateErrorImage.tsx';
import type { WorkItemCreationProps } from '../../__types__/WorkItemCreationError.ts';
import { useIssueCreateSidePanelContext } from '../../providers/IssueCreateSidePanelContextProvider.tsx';
import { useBulkCreateContext } from '../../providers/BulkCreateContextProvider.tsx';

const errorTryAgainTestId = 'error-try-again-button';
const errorCreateManuallyTestId = 'error-create-manually-button';

const errorContainerStyle = xcss({
	flexGrow: '1',
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'center',
	justifyContent: 'flex-start',
	paddingInline: 'space.800',
});

const errorDescriptionStyle = xcss({
	paddingBlock: 'space.300',
	font: token('font.body.large'),
	fontWeight: token('font.weight.regular'),
	color: 'color.text.subtle',
});

const buttonLayoutStyle = xcss({
	width: '100%',
	paddingBlock: 'space.050',
});

export const WorkItemCreationError = ({
	creationSource,
	errorMessage,
	errorMessageValues,
	isCreatedWithAi,
	onTryAgain,
	onCreateManually,
}: WorkItemCreationProps) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [{ pageMode, hasEditPermission }] = useIssueCreateSidePanelContext();
	const [{ isCreatedFromActionItems, isCreatedFromTable }] = useBulkCreateContext();

	useEffect(() => {
		createAnalyticsEvent({
			type: 'sendScreenEvent',
			data: {
				name: isCreatedWithAi ? 'ai creation error screen' : 'creation error screen',
				source: creationSource,
				attributes: {
					pageMode,
					hasEditPermission,
					isCreatedFromActionItems,
					isCreatedFromTable,
				},
			},
		}).fire();
	}, [
		createAnalyticsEvent,
		creationSource,
		pageMode,
		isCreatedWithAi,
		hasEditPermission,
		isCreatedFromActionItems,
		isCreatedFromTable,
	]);

	return (
		<Box xcss={errorContainerStyle}>
			<Box>
				<WorkItemCreateErrorImage />
			</Box>
			<Box testId="error-description" xcss={errorDescriptionStyle}>
				<FormattedMessage {...errorMessage} values={errorMessageValues} />
			</Box>
			{onTryAgain && (
				<Box xcss={buttonLayoutStyle}>
					<Button testId={errorTryAgainTestId} onClick={onTryAgain}>
						<FormattedMessage {...messages.errorTryAgainButton} />
					</Button>
				</Box>
			)}
			{onCreateManually && (
				<Box xcss={buttonLayoutStyle}>
					<Button testId={errorCreateManuallyTestId} onClick={onCreateManually}>
						<FormattedMessage {...messages.errorCreateManuallyButton} />
					</Button>
				</Box>
			)}
		</Box>
	);
};
